/**
 * What the playbook chat reads from its own thread (SAL-3310). The page sends
 * every message back to /api/playbook/chat and saves the thread, so the
 * search tool's outputs on an assistant message are both where an answer's
 * links are checked and the bulk of what would be stored; a saved thread
 * keeps only what checking needs. Client-safe: the page, the surface row and
 * the tool all import it.
 */
import { listToolParts } from "@/lib/ai-message-utils";
import {
  isPlaybookAnchor,
  keepKnownPlaybookLinks,
  keepListedWebLinks,
  listMarkdownLinkTargets,
  listPlaybookLinkTargets,
} from "@/lib/playbook/item-links";

/** The playbook surface's one tool (lib/agent-tools/playbook-tool-definitions.js). */
export const SEARCH_PLAYBOOK_TOOL = "search_playbook";

/**
 * The anchors a returned section can be linked by: its own id and every item
 * in it, whether the item is in its content, a loaded entry (`anchor`) or a
 * matched entry (`link`).
 */
export function listSectionAnchors(section) {
  if (Array.isArray(section?.anchors)) return section.anchors;
  const entries = section?.entries || [];
  const anchors = [
    section?.sectionId,
    ...listPlaybookLinkTargets(section?.content),
    ...entries.flatMap((entry) => [entry.anchor, ...listPlaybookLinkTargets(entry.link)]),
  ];
  return [...new Set(anchors.filter(isPlaybookAnchor))];
}

// Every finished search on one assistant message that found the playbook.
function listSearchParts(message) {
  return listToolParts(message, SEARCH_PLAYBOOK_TOOL).filter((part) => part.output?.success && part.output.data);
}

/** The section anchors a search's catalog lists: every section the viewer's page has. */
function listCatalogAnchors(search) {
  return (search.catalog || []).flatMap((entry) => listPlaybookLinkTargets(entry)).filter(isPlaybookAnchor);
}

/**
 * An assistant message as the page shows it: links kept only to anchors the
 * search returned (a section in its catalog, or an item in a section it
 * read) and to web results the search listed; any other link becomes its
 * label. The sections and results the answer links to are its sources. A
 * message saved before the search tool carries the sources its action wrote,
 * and `knownAnchors` names what the page can scroll to besides.
 */
export function readPlaybookAnswer(message, { knownAnchors = new Set() } = {}) {
  const searches = listSearchParts(message).map((part) => part.output.data);
  const sections = searches.flatMap((search) => [...(search.sections || []), ...(search.matches || [])]);
  const webResults = searches.flatMap((search) => search.webResults || []);
  const anchors = new Set([...knownAnchors, ...searches.flatMap(listCatalogAnchors), ...sections.flatMap(listSectionAnchors)]);
  const titlesByUrl = new Map(webResults.map((result) => [result.url, result.title]));
  const content = keepListedWebLinks(keepKnownPlaybookLinks(message?.content, anchors), titlesByUrl);
  if (!searches.length) {
    return { content, sources: Array.isArray(message?.sources) ? message.sources : [] };
  }
  const linked = new Set(listMarkdownLinkTargets(content));
  return {
    content,
    sources: [
      ...sections
        .filter((section) => listSectionAnchors(section).some((anchor) => linked.has(`#${anchor}`)))
        .map(({ sectionId, sectionTitle }) => ({ sectionId, sectionTitle })),
      ...webResults
        .filter((result) => linked.has(result.url))
        .map(({ title, url }) => ({ sectionTitle: title, url })),
    ],
  };
}

const compactSection = (section) => ({
  sectionId: section.sectionId,
  sectionTitle: section.sectionTitle,
  anchors: listSectionAnchors(section),
});

// A saved search keeps what readPlaybookAnswer needs: its catalog, which
// sections, matched entries and results it returned and their anchors, not
// their text.
function compactSearch({ catalog = [], sections = [], matches = [], webResults = [] }) {
  return {
    catalog,
    sections: sections.map(compactSection),
    matches: matches.map(compactSection),
    webResults: webResults.map(({ title, url }) => ({ title, url })),
  };
}

/** The thread with each search's text dropped, for the saved copy (one per studio). */
export function compactPlaybookThread(messages = []) {
  return messages.map((message) => {
    const searches = new Set(listSearchParts(message));
    if (!searches.size) return message;
    return {
      ...message,
      parts: message.parts.map((part) =>
        searches.has(part)
          ? { ...part, output: { ...part.output, data: compactSearch(part.output.data) } }
          : part,
      ),
    };
  });
}
